import { type CreateDiscountDTO, type DiscountDTO } from "../../entities/Discount";
import {
  type ChangeInstallmentPlanPayload,
  type ChangeInstallmentPlanSummary,
} from "../../entities/InstallmentPlanChange";
import { type CreateProductDTO, type UpdateProductDTO, type UpdateProductPriceDTO } from "../../entities/Product";
import {
  type GrantProductAccessRequest,
  type GrantProductAccessResponse,
  type ImportPreviewRequest,
  type ImportPreviewResponse,
  type ProductClientsImportAggregation,
  type ProductImportSummary,
  type RetryProductImportResponse,
} from "../../entities/ProductClientToImport";
import { type CloudFunctionDefinition, type RouteMeta, typed } from "../cloudFunctions";

/**
 * products-service — the busiest surface, so the registry starts here
 * (TRE-1684). Bodies are typed where a client calls the route; the rest carry
 * `unknown` until someone needs them, which keeps the declaration honest instead
 * of inventing shapes.
 *
 * `auth` mirrors the middleware the service mounts today. Nothing enforces it
 * yet — `createHttpFunction` (TRE-1685) is what will.
 */
export const productsRoutes = {
  healthCheck: typed<void, string>()({ method: "get", path: "/health-check", auth: "public" }),

  // products
  addProduct: typed<CreateProductDTO, void>()({ method: "post", path: "/add-product", auth: "user" }),
  updateProduct: typed<UpdateProductDTO, void>()({ method: "post", path: "/update-product", auth: "user" }),
  duplicateProduct: typed<unknown, void>()({ method: "post", path: "/duplicate-product", auth: "user" }),
  archiveProduct: typed<{ id: string }, void>()({ method: "post", path: "/archive-product", auth: "user" }),
  removeProduct: typed<{ id: string }, void>()({ method: "post", path: "/remove-product", auth: "user" }),
  updateProductPrices: typed<{ prices: UpdateProductPriceDTO[] }, void>()({
    method: "post",
    path: "/update-product-prices",
    auth: "user",
  }),
  updateProductSlug: typed<{ productId: string; slug: string }, void>()({
    method: "post",
    path: "/update-product-slug",
    auth: "user",
  }),
  importClientsToProduct: typed<
    { productId: string; clientsToImport: unknown[] },
    { status: string; importId: string }
  >()({
    method: "post",
    path: "/import-clients-to-product",
    auth: "user",
  }),
  // TRE-2110: the product's import history, read by the admin (the tables are
  // RLS-locked to service_role) and by the stepper's summary step.
  productImports: typed<void, ProductClientsImportAggregation[], { productId: string }>()({
    method: "get",
    path: "/products/:productId/imports",
    auth: "user",
  }),
  productImport: typed<void, ProductImportSummary, { productId: string; importId: string }>()({
    method: "get",
    path: "/products/:productId/imports/:importId",
    auth: "user",
  }),
  grantProductAccess: typed<GrantProductAccessRequest, GrantProductAccessResponse, { productId: string }>()({
    method: "post",
    path: "/products/:productId/clients/grant",
    auth: "user",
  }),
  previewProductImport: typed<ImportPreviewRequest, ImportPreviewResponse, { productId: string }>()({
    method: "post",
    path: "/products/:productId/imports/preview",
    auth: "user",
  }),
  retryProductImport: typed<void, RetryProductImportResponse, { productId: string; importId: string }>()({
    method: "post",
    path: "/products/:productId/imports/:importId/retry",
    auth: "user",
  }),
  generateCheckoutCode: typed<unknown, { code: string }>()({
    method: "post",
    path: "/generate-checkout-code",
    auth: "user",
  }),
  revalidateProductCart: typed<unknown, void>()({ method: "post", path: "/revalidate-product-cart", auth: "supabase" }),

  // discounts
  createDiscount: typed<CreateDiscountDTO, void>()({ method: "post", path: "/create-discount", auth: "user" }),
  updateDiscount: typed<DiscountDTO, void>()({ method: "post", path: "/update-discount", auth: "user" }),

  // orders and payments
  createOrder: typed<unknown, unknown>()({ method: "post", path: "/create-order", auth: "public" }),
  createOrderForFunnelStep: typed<unknown, unknown, { funnelId: string; funnelStep: string }>()({
    method: "post",
    path: "/create-order/funnel/:funnelId/:funnelStep",
    auth: "public",
  }),
  updateOrder: typed<unknown, unknown>()({ method: "post", path: "/update-order", auth: "task" }),
  payBlikInstallment: typed<unknown, unknown>()({ method: "post", path: "/pay-blik-installment", auth: "public" }),
  blikInstallmentInfo: typed<void, unknown>()({ method: "get", path: "/blik-installment-info", auth: "public" }),
  blikRecurringRetry: typed<unknown, unknown>()({ method: "post", path: "/blik-recurring-retry", auth: "task" }),
  activateTrial: typed<unknown, unknown>()({ method: "post", path: "/activate-trial", auth: "public" }),
  manualPaymentCreateOrder: typed<unknown, unknown>()({
    method: "post",
    path: "/manual-payment/create-order",
    auth: "user",
  }),
  manualPaymentConfirm: typed<unknown, unknown>()({
    method: "post",
    path: "/manual-payment/confirm",
    auth: "permission",
  }),
  manualPaymentConfirmAsSeller: typed<unknown, unknown>()({
    method: "post",
    path: "/manual-payment/confirm-as-seller",
    auth: "user",
  }),
  manualPaymentPendingForSeller: typed<unknown, unknown>()({
    method: "post",
    path: "/manual-payment/pending-for-seller",
    auth: "user",
  }),
  manualPaymentFailed: typed<unknown, unknown>()({
    method: "post",
    path: "/manual-payment/failed",
    auth: "permission",
  }),
  cancelOrder: typed<unknown, unknown>()({
    method: "post",
    path: "/manual-payment/cancel",
    auth: "permission",
  }),
  migrateSubscriptionToManualPayments: typed<unknown, unknown>()({
    method: "post",
    path: "/migrate-subscription-to-manual-payments",
    auth: "permission",
  }),

  // product clients
  archiveProductClient: typed<{ id: string; productId: string }, void>()({
    method: "post",
    path: "/archive-product-client",
    auth: "user",
  }),
  changeProductClientEmail: typed<unknown, void>()({
    method: "post",
    path: "/change-product-client-email",
    auth: "user",
  }),
  updateClientPaymentMethod: typed<unknown, void>()({
    method: "post",
    path: "/update-client-payment-method",
    auth: "user",
  }),
  convertManualPaymentToCard: typed<unknown, void>()({
    method: "post",
    path: "/convert-manual-payment-to-card",
    auth: "user",
  }),
  cancelSubscription: typed<unknown, unknown>()({ method: "post", path: "/cancel-subscription", auth: "user" }),
  resumeSubscription: typed<unknown, unknown>()({ method: "post", path: "/resume-subscription", auth: "user" }),
  extendClientAccessPeriod: typed<unknown, void>()({
    method: "post",
    path: "/extend-client-access-period",
    auth: "user",
  }),
  // Admin change of installment count/amount on an active installment order (TRE-1764).
  changeInstallmentPlan: typed<ChangeInstallmentPlanPayload, ChangeInstallmentPlanSummary>()({
    method: "post",
    path: "/change-installment-plan",
    auth: "permission",
  }),
  requestAccountDeletion: typed<unknown, unknown>()({
    method: "post",
    path: "/request-account-deletion",
    auth: "public",
  }),
  archiveProductClientTask: typed<unknown, unknown>()({
    method: "post",
    path: "/task/archive-product-client",
    auth: "task",
  }),

  // payoff
  payoffQuote: typed<void, unknown>()({ method: "get", path: "/payoff/quote", auth: "optional" }),
  payoffExecute: typed<unknown, unknown>()({ method: "post", path: "/payoff/execute", auth: "public" }),
  payoffExchangeCode: typed<unknown, unknown>()({ method: "post", path: "/payoff/exchange-code", auth: "public" }),
  // Long-lived entry token from the offer email (TRE-1713) — exchanged for the
  // same short-lived payoff session as the single-use magic-link code.
  payoffExchangeEmailToken: typed<unknown, unknown>()({
    method: "post",
    path: "/payoff/exchange-email-token",
    auth: "public",
  }),
  payoffOffers: typed<void, unknown>()({ method: "get", path: "/payoff/offers", auth: "user" }),
  payoffMagicLink: typed<unknown, { url: string; expiresAt: number }>()({
    method: "post",
    path: "/payoff/magic-link",
    auth: "user",
  }),
  payoffAdminMagicLink: typed<unknown, { url: string; expiresAt: number }>()({
    method: "post",
    path: "/payoff/admin-magic-link",
    auth: "user",
  }),
  // Individually granted payoff offers (TRE-1713). Authenticated as an app
  // user; the staff-role guard lives in the controllers, like the admin
  // magic-link route above.
  payoffGrantCandidates: typed<void, unknown>()({
    method: "get",
    path: "/payoff/grant-candidates",
    auth: "user",
  }),
  payoffGrantOffer: typed<unknown, unknown>()({ method: "post", path: "/payoff/grant-offer", auth: "user" }),
  payoffRevokeOffer: typed<unknown, unknown>()({ method: "post", path: "/payoff/revoke-offer", auth: "user" }),
  // Payoff paid by transfer and booked by staff (TRE-1737) — same staff-role
  // guard in the controllers as the grant routes above.
  payoffOfflineSettlement: typed<void, unknown>()({
    method: "get",
    path: "/payoff/offline-settlement",
    auth: "user",
  }),
  payoffSettleOffline: typed<unknown, unknown>()({ method: "post", path: "/payoff/settle-offline", auth: "user" }),

  // offers and add-ons
  addons: typed<void, unknown>()({ method: "get", path: "/addons", auth: "user" }),
  offers: typed<void, unknown>()({ method: "get", path: "/offers", auth: "user" }),
  offersMagicLink: typed<unknown, unknown>()({ method: "post", path: "/offers/magic-link", auth: "user" }),

  // reporting
  funnelStats: typed<void, unknown>()({ method: "get", path: "/funnel/stats", auth: "permission" }),
  renewalCohortStats: typed<void, unknown, { productId: string }>()({
    method: "get",
    path: "/products/:productId/renewal-cohort-stats",
    auth: "permission",
  }),
  collectionsDashboard: typed<void, unknown>()({ method: "get", path: "/collections/dashboard", auth: "permission" }),
  collectionsCases: typed<void, unknown>()({ method: "get", path: "/collections/cases", auth: "permission" }),
  collectionsCase: typed<void, unknown, { caseId: string }>()({
    method: "get",
    path: "/collections/cases/:caseId",
    auth: "permission",
  }),
  completeCollectionsTask: typed<unknown, unknown, { caseId: string; taskId: string }>()({
    method: "post",
    path: "/collections/cases/:caseId/tasks/:taskId/complete",
    auth: "permission",
  }),
  recordCollectionsCaseAction: typed<unknown, unknown, { caseId: string }>()({
    method: "post",
    path: "/collections/cases/:caseId/actions",
    auth: "permission",
  }),
  syncCollectionsCases: typed<void, unknown>()({ method: "post", path: "/collections/sync", auth: "permission" }),
  runCollectionsAutomation: typed<unknown, unknown>()({
    method: "post",
    path: "/collections/automation/run",
    auth: "permission",
  }),
  updateCollectionsSettings: typed<unknown, unknown>()({
    method: "put",
    path: "/collections/settings",
    auth: "user",
  }),
  importCollectionsBankCsv: typed<unknown, unknown>()({
    method: "post",
    path: "/collections/bank/import",
    auth: "permission",
  }),
  collectionsBankTransactions: typed<void, unknown>()({
    method: "get",
    path: "/collections/bank/transactions",
    auth: "permission",
  }),
  settleCollectionsBankTransaction: typed<unknown, unknown, { transactionId: string }>()({
    method: "post",
    path: "/collections/bank/transactions/:transactionId/settle",
    auth: "permission",
  }),
  ignoreCollectionsBankTransaction: typed<unknown, unknown, { transactionId: string }>()({
    method: "post",
    path: "/collections/bank/transactions/:transactionId/ignore",
    auth: "permission",
  }),
  draftCollectionsAssistant: typed<unknown, unknown, { caseId: string }>()({
    method: "post",
    path: "/collections/cases/:caseId/assistant",
    auth: "permission",
  }),
  sendCollectionsCustomMessage: typed<unknown, unknown, { caseId: string }>()({
    method: "post",
    path: "/collections/cases/:caseId/messages",
    auth: "permission",
  }),
  collectionsCaseLetter: typed<void, unknown, { caseId: string }>()({
    method: "get",
    path: "/collections/cases/:caseId/letter",
    auth: "permission",
  }),
  updateClientCollectionsStage: typed<unknown, unknown>()({
    method: "post",
    path: "/clients/collections-stage",
    auth: "permission",
  }),
  installmentCollectionStats: typed<void, unknown>()({
    method: "get",
    path: "/installments/collection-stats",
    auth: "permission",
  }),
  checkoutSessionStats: typed<void, unknown>()({ method: "get", path: "/checkout-sessions/stats", auth: "permission" }),
  replicateRenewalCohortStatsToRedis: typed<unknown, unknown>()({
    method: "post",
    path: "/replicate-renewal-cohort-stats-to-redis",
    auth: "supabase",
  }),
  replicateSalesFunnelStepsStatsToRedis: typed<unknown, unknown>()({
    method: "post",
    path: "/replicate-sales-funnel-steps-stats-to-redis",
    auth: "supabase",
  }),

  // inbound webhooks — signature-verified inside the handler, hence `public`
  stripeWebhook: typed<unknown, unknown>()({ method: "post", path: "/webhook", auth: "public" }),
  stripeWebhookLegacy: typed<unknown, unknown>()({ method: "post", path: "/webhook-legacy", auth: "public" }),
  stripeWebhookPartnership: typed<unknown, unknown>()({
    method: "post",
    path: "/webhook-partnership",
    auth: "public",
  }),
  stripeWebhookConnected: typed<unknown, unknown>()({ method: "post", path: "/webhook-connected", auth: "public" }),
  tpayNotification: typed<unknown, unknown, { productId: string; orderId: string }>()({
    method: "post",
    path: "/tpay-notification/:productId/:orderId",
    auth: "public",
  }),
  tpayNotificationLegacy: typed<unknown, unknown, { posId: string; productId: string; orderId: string }>()({
    method: "post",
    path: "/tpay-notification-legacy/:posId/:productId/:orderId",
    auth: "public",
  }),
  tpayNotificationPartnership: typed<unknown, unknown, { posId: string; productId: string; orderId: string }>()({
    method: "post",
    path: "/tpay-notification-partnership/:posId/:productId/:orderId",
    auth: "public",
  }),
  calendlySalesCallMarkAttended: typed<unknown, unknown>()({
    method: "post",
    path: "/calendly/sales-call/mark-attended",
    auth: "public",
  }),
} satisfies Record<string, RouteMeta>;

export const productsFunction: CloudFunctionDefinition<typeof productsRoutes> = {
  functionName: "products-api",
  region: "europe-west3",
  version: "v1",
  routes: productsRoutes,
};
